import { fn } from "../utils";
import { Observable, IPageQueryConstraint, IPageMetaData, TPagePropertyAccess } from "../nav";
import { IMarkupCustomization, IMarkupCustomizationMaps } from "../navkendo";
import { NavSettingsService } from "../services/settingsservice";
import { INavCustomization } from "../services/settingsservice";
import { CompatibilityService } from "../services/compatibilityservice";
import { Pages as rwf3pages, IFDRW_CustomCaption, IFDDI_Wizard_Create_New_DMS_Document, Accounting, Einnahme_Ausgabe_47, IFDRW_Anordnungsblatt, IFDDI_DMS_Purchase_Document } from './rwfpages.generated';
import { RwfBootstrap, IRwfBootstrapParameters } from "./rwf.bootsrap";
import { IsKameral } from "./rwf.functions";
import { FDRW_Workflow_Running_Instances, FDDI_Wizard_Create_New_DMS_Document, FDRW_Anordnungsblatt } from "@common/rwf/rwf.const";
import { IFDRW_Anordnungsblatt_AT } from './rwfpages.generated.addon';

const wfMyInstances = rwf3pages.FDRW_Workflow_User_Instances_List,
  wfAllInstances = rwf3pages.FDRW_Workflow_Invoice_Ledger;

/** Global Applicaction Settings ["App_data\...KWF/KWFApplicationSettings"] Access  */
export class RwfAppSettings extends Observable {
  constructor(readonly $c: CompatibilityService, readonly NavSettings: NavSettingsService, readonly NavCustomization: INavCustomization, readonly RwfBootstrap: RwfBootstrap) {
    super();

    RwfBootstrap.OnResolved(($rwf: IRwfBootstrapParameters) => {
      // adjust Customization
      NavCustomization.ConfigName = $rwf.RwfSettings.ConfigName || NavCustomization.ConfigName;
      const NavUrl = NavCustomization.NavUrl = $rwf.NavUrlInfo.FullUrl;
      const NavClient = $c.resolve("NavClient");

      NavClient.RegisterPrepareClient(e => InitializeRWFPages($rwf, e.Page));
      // Adjust Custom Names from Workflow Setup
      return NavClient.ReadMultiple<IFDRW_CustomCaption>({
        PageName: rwf3pages.FDRW_CustomCaption.PageName, PageCount: -1, Constraints: []
      }, NavUrl, true).then((info) => {
        if (info.Data.length > 0) {
          let customnameMap = fn.ToDictionaryEx(info.Data, item => item.Name, item => item.Caption);
          NavClient.RegisterPrepareClient(e => {
            if (!e || !(e.Page || e.IsCustom))
              return;
            var pages = e.Page.Properties.map(item => item.ArrayProperties).filter(fn.isSet);
            pages.unshift(e.Page);
            pages.forEach(page => {
              page.Properties.forEach(prop => {
                var customTitle = customnameMap[prop.Name];
                if (!customTitle && /^IAXCustProp\d$/.test(prop.Name)) {
                  customTitle = customnameMap[prop.Name.replace(/^IAX/, "")];
                }
                if (customTitle)
                  prop.Title = customTitle;
              });
            });
          });
        }
      }, fn.noop);
      // Register CustomCaptions:
    });
  }

  /** Promise to Retrieve Searver Stored Application Settings (JSON) */
  Get() {
    if (this.getGlobalConfig)
      return this.getGlobalConfig;

    var settings = this.NavSettings.GetGlobalSetting<IRwfApplicationSettings>("KWF/KWFApplicationSettings");
    this.getGlobalConfig = this.RwfBootstrap.Resolve.then(() => settings.LoadValue().then((data) => {
      var saveValues = false;
      if (!data) {
        data = DefaultApplicationSettings;
        saveValues = true;
      } else {
        saveValues = fn.ExtendObject(data, DefaultApplicationSettings);

        // FixUp adjusted Changes
        const cc = data.CustomCaptions,
          ccx = cc && cc.Search_Kam;
        if (ccx && (ccx.Eingang == "Einahme")) {
          ccx.Eingang = "Einnahme";
          saveValues = true;
        }

        const sf = data.SearchFields;
        if (sf) {
          // As we initially deploy this, adjustments must be manually merged (unfortunately)
          var forceNames = [5, 4, 3, 2].map(idx => "Überweisungstext_" + idx);
          forceNames.push("Name_2", "Search_Name", "DocumentTotalAmount", "Zahlungsformcode", "EntryNo", "Purchaser_Code", "VAT_Reporting_Date", "Datum_der_Verbuchung", "View");

          forceNames.forEach(fName => {
            if (sf.indexOf(fName) < 0) {
              sf.splice(0, 0, fName);
              saveValues = true;
            }
          });
        }

        // FDRW_Workflow_Running_Instances as User Cols
        const userCols = data.UserColumns || [];
        userCols.forEach(col => {
          const p = col.Page;
          if (typeof p !== "string" && p.length && p.indexOf(wfMyInstances.PageName) >= 0) {
            if (p.indexOf(FDRW_Workflow_Running_Instances) < 0) {
              p.push(FDRW_Workflow_Running_Instances);
              saveValues = true;
            }
          }
        });
      }

      if (saveValues) {
        settings.SaveValue(data, true);
      }

      // Transfer Lookup Settings
      var custom = this.NavCustomization;
      if (custom && data.LookupSettings)
        custom.LookupSettings = fn.clone(data.LookupSettings);

      return this.loadedAppSettings = data;
    }));
    return this.getGlobalConfig;
  }
  private getGlobalConfig: PromiseLike<IRwfApplicationSettings>;
  private loadedAppSettings: IRwfApplicationSettings;

  /** Loaded AppSettings, Accessible only after Resolve! */
  public get LoadedAppSettings() {
    const result = this.loadedAppSettings;
    if (!result) {
      throw new Error('AppSettings not yet loaded');
    };
    return result;
  }

  /** Save Current Application Config (ADMIN) */
  public Save(store: IRwfApplicationSettings) {
    var settings = this.NavSettings.GetGlobalSetting<IRwfApplicationSettings>("KWF/KWFApplicationSettings");
    return settings.SaveValue(store, false).then((data) => {
      this.loadedAppSettings = data;
      delete this.getGlobalConfig;
      // this.trigger(Event_AppSettingsChanged);
      return this.loadedAppSettings;
    });
  }

  /** Get Effective Search Pages for Current Context (Kameral or not) */
  public get EffectiveSearchPages() {
    return this.Get().then(c => {
      return this.RwfBootstrap.Resolve.then(rwf3 => {
        const isKameral = IsKameral(rwf3.RwfSetup);
        return isKameral ? c.SearchPages_KAM : c.SearchPages;
      });
    });
  }
}

/**
 * RWF Custom Client Side per Page Initializations
 * TODO: Move Custom Initializations from 'KWFEdit' to here ...
 * */
function InitializeRWFPages(rwf: IRwfBootstrapParameters, Page: IPageMetaData) {
  switch (Page?.PageName) {
    case "FDDI_DMS_Purchase_Document":
      // Einkauf: restrict Anlagenbuchungsart per document scenario.
      const filterHookMarker = "__purchFaPostingFilterHooked";
      const applyPurchLinePostingTypeFilter = (item: any) => {
        const root = <any>item?.Root;
        const lineData = root?.GetLineData && root.GetLineData("PurchLines");
        const lines = lineData?.Items || [];
        if (!lines.length)
          return;

        const model = <IFDDI_DMS_Purchase_Document>root.Model;
        const isCreditMemo = model.Document_Type === 3 /** Credit_Memo */;
        const creationTypeName = root?.GetValueEx && root.GetValueEx("Creation_Type");
        const creationType = <number><any>model.Creation_Type;
        const isPurchOrderOrChange =
          creationTypeName === "Reservation" ||
          creationTypeName === "PurchOrderChange" ||
          creationType === 3 /** Reservation */ ||
          creationType === 5 /** PurchOrderChange */;

        const lineProp = lineData?.Property?.ArrayProperties?.ResolveProperty("FA_Posting_Type", false);
        const linePropAny = <any>lineProp;
        const originalMappings = <any[]>(linePropAny?.__allEnumMappings || lineProp?.EnumMappings || []);
        if (lineProp && !linePropAny.__allEnumMappings) {
          linePropAny.__allEnumMappings = originalMappings.slice();
        }
        const allMappings = <any[]>linePropAny.__allEnumMappings || [];
        const allowedEnums = isPurchOrderOrChange
          ? ["_blank_", "Acquisition_Cost", "Maintenance"]
          : isCreditMemo
            ? ["_blank_", "Acquisition_Cost", "Disposal", "Partial_Disposal", "IAX_Disposal", "IAX_Partial_Disposal"]
            : ["_blank_", "Acquisition_Cost", "Maintenance", "Disposal", "Partial_Disposal", "IAX_Disposal", "IAX_Partial_Disposal"];
        const valid = allMappings
          .filter(m => m?.IsEmpty === true || allowedEnums.indexOf(m?.Enum) >= 0)
          .map(m => m.Value);
        const invalid = allMappings
          .map(m => m.Value)
          .filter(value => valid.indexOf(value) < 0);

        // Fallback for line editors that ignore dynamic invalid-enum events:
        // constrain metadata enum list directly for PurchLines.FA_Posting_Type.
        if (lineProp && linePropAny.__allEnumMappings) {
          const filtered = (<any[]>linePropAny.__allEnumMappings).filter(m => m?.IsEmpty === true || allowedEnums.indexOf(m?.Enum) >= 0);
          lineProp.EnumMappings = <any>filtered;
          lineProp.EnumMappings.Empty = filtered.filter(m => m.IsEmpty === true || m.Enum == "_blank_")[0];
        }

        lines.forEach(line => line.GetCustomInvalidEnumData("FA_Posting_Type")?.SetInvalid(invalid));
      };
      const ensurePurchLineFilterHooks = (item: any) => {
        const root = <any>item?.Root;
        if (!root)
          return;

        if (!root[filterHookMarker]) {
          root[filterHookMarker] = true;
          const lineData = root?.GetLineData && root.GetLineData("PurchLines");
          lineData?.RegisterCountChanged(() => applyPurchLinePostingTypeFilter(root));
        }

        applyPurchLinePostingTypeFilter(root);
      };
      Page.RegisterPropertyChanged("*", e => {
        ensurePurchLineFilterHooks(e.Item);
      });
      break;
    case FDDI_Wizard_Create_New_DMS_Document:
      // Enum Value Bestellung nur sinnvoll für Einkaufsdokumente
      Page.RegisterInvalidateEnum<IFDDI_Wizard_Create_New_DMS_Document>("Accounting", "Document_Type",
        wzrdModel => wzrdModel.Accounting == Accounting.Customer ? [1/**Order */] : []);
      break;
    case FDRW_Anordnungsblatt:
      Page.RegisterInvalidateEnum<IFDRW_Anordnungsblatt_AT>("Ein_Aus", "FA_Posting_Type",
        (model: IFDRW_Anordnungsblatt_AT) => model.Ein_Aus == Einnahme_Ausgabe_47.Ausgabe ?
          [1 /** Acquisition_Cost */] :
          [7 /** Verkauf */, 13 /** Teilverkauf */, 10 /** Zuschusseingang */]
        , true /** reverse logic */);
      break;
    default:
      break;
  }

  var custPropswithDynamicLookup = Page.Properties.filter(item => /^(IAX)?CustProp\d$/.exec(item.Name)?.length > 0)
    .map(item => {
      var no = parseInt(item.Name.replace(/^IAX/, "").substr("CustProp".length));
      return { No: no, Active: rwf.RwfSetup["UsePredefinedCustProp" + no] === true, Item: item }
    }).filter(item => item.Active);
  custPropswithDynamicLookup.forEach(propInfo => {
    const prop = propInfo.Item;
    prop.HasLookup = true;
    prop.StaticLookup = {
      IsStatic: true,
      SearchField: undefined,
      Name: undefined,
      Title: prop.Title,
      TargetField: "Description",
      TargetPageName: "FDRW_CustomProperties",
      DropDownFields: [{ Name: "Description", Title: "Beschreibung" }],
      Constraints: [{ FieldName: "CustPropId", Value: "CustProp" + propInfo.No }],
    };
  });

}

/** User Desk Available Search Pages */
export type TSearchPage = "RunningInvoiceInbox" | "RunningInvoiceOutbox" | "RunningInvoiceInOut" | "ArchivedInvoiceInbox" | "ArchivedInvoiceOutbox" | "RunningBWF" | "ArchivedBWF";

/** Fields to be displayed in preview popup */
export interface IPreviewFieldSetting {
  Title: string[];
  Content: (string | string[])[][];
}
export interface IKWFSearchPage<TPage = any> {
  PageTitle: string;
  /** Possibility to Disable Certain Pages from UI (e.g. KTGR) */
  Disabled?: boolean;
  /** NavPageName : Possibility to Redirect NAV Retrieval to other Page Name */
  NavPageName: string;
  /** Optional Global Filters for All Pages in Search Filters  */
  QueryConstraints: IPageQueryConstraint<TPage>[];
}

//MSQQQQ -> CommonConrols
/** Settings for Tiles */
interface ITileSettings {
  Thresholds: {
    Small: number, Medium: number, Large: number
  };
}

/** Server Stored Application Settings */
export interface IRwfApplicationSettings {

  /** Defined Pages for Search (RWF2, Doppisch) */
  SearchPages: { [pageName in TSearchPage]: IKWFSearchPage };

  /** Defined Pages for Kameral Search */
  SearchPages_KAM: { [pageName in TSearchPage]: IKWFSearchPage
  };

  /*Explicit constraint of SearchFields, if Empty: Use Intersection of Properties ( with AliasNames) */
  SearchFields: string[];

  /* Customizations for Rows in UserDesk( 'MyInvoices'/ 'AllInvoices') */
  RowCustomizations?: IMarkupCustomization[];

  /** Customizations for Cells in UserDesk */
  FieldCustomizations?: IMarkupCustomizationMaps;

  /** Default Settings for Instarsys Signature Options */
  DefaultSignatureSettings: any;

  /** Columns to be treated as user Columns (possible in KWF3 only so far!) */
  UserColumns: Array<{ Page: string | string[], Fields: string[] }>;

  /** Stack amount that define the stack height. Default = {Small: 10, Medium: 100, Large: 500} */
  TileSettings?: ITileSettings;

  /** Default Lookup Settings */
  LookupSettings: {
    MaxRead: number;
    MinSearch: number;
  };

  FormSettings?: {
    SaveTemp?: {
      Hidden?: boolean
    };
  }

  CustomCaptions: {
    Search: {
      Eingang: string,
      Ausgang: string,
      Auftrag: string,
    },
    Search_Kam: {
      Eingang: string,
      Ausgang: string,
      Auftrag: string
    }
  };
}

/** Default Application Settings: Will be stored in  [CustomizationPath(Default="App_Data")]"/GlobalSettings/KWF/KWFApplicationSettings.json"
 * changes to the default can be made there
 * at the Moment: Extension here will be automatically stored in server Settings on first call (if user has right to save the server file) */
export const DefaultApplicationSettings = <IRwfApplicationSettings>{
  SearchPages: {
    "RunningInvoiceInbox": {
      PageTitle: "Eingang (in Arbeit)",
      Disabled: false,
      NavPageName: rwf3pages.FDRW_Workflow_Invoice_Ledger.PageName,
      QueryConstraints: [{ FieldName: rwf3pages.FDRW_Workflow_Invoice_Ledger.Fields.Accounting, Value: 'Vendor' }]
    },
    "RunningInvoiceOutbox": {
      PageTitle: "Ausgang (in Arbeit)",
      Disabled: false,
      NavPageName: rwf3pages.FDRW_Workflow_Invoice_Ledger.PageName,
      QueryConstraints: [{ FieldName: rwf3pages.FDRW_Workflow_Invoice_Ledger.Fields.Accounting, Value: 'Customer' }]
    },
    "ArchivedInvoiceInbox": {
      PageTitle: "Eingang (gebucht)",
      Disabled: false,
      NavPageName: "FDRW_Purchase_Search",
      QueryConstraints: []
    },
    "ArchivedInvoiceOutbox": {
      PageTitle: "Ausgang (gebucht)",
      Disabled: false,
      NavPageName: "FDRW_Sales_Search",
      QueryConstraints: []
    },
    "RunningBWF": {
      PageTitle: "Bestellungen (in Arbeit)",
      Disabled: false,
      NavPageName: rwf3pages.FDRW_Workflow_Invoice_Ledger.PageName
    },
    "ArchivedBWF": {
      PageTitle: "Bestellungen (gebucht)",
      Disabled: false,
      NavPageName: "FDRW_PurchaseOrder_Search",
      QueryConstraints: []
    }
  },
  SearchPages_KAM: {
    "RunningInvoiceInbox": {
      PageTitle: "Einnahme (in Arbeit)",
      Disabled: false,
      NavPageName: rwf3pages.FDRW_Workflow_Invoice_Ledger.PageName,
      QueryConstraints: [{ FieldName: rwf3pages.FDRW_Workflow_Invoice_Ledger.Fields.DocumentType, Value: '@*Einnahme*' }]
    },
    "RunningInvoiceOutbox": {
      PageTitle: "Ausgabe (in Arbeit)",
      Disabled: false,
      NavPageName: rwf3pages.FDRW_Workflow_Invoice_Ledger.PageName,
      QueryConstraints: [{ FieldName: rwf3pages.FDRW_Workflow_Invoice_Ledger.Fields.DocumentType, Value: '@*Ausgabe*' }]
    },
    "RunningInvoiceInOut": {
      PageTitle: "Verrechnung (in Arbeit)",
      Disabled: false,
      NavPageName: rwf3pages.FDRW_Workflow_Invoice_Ledger.PageName,
      QueryConstraints: [{ FieldName: rwf3pages.FDRW_Workflow_Invoice_Ledger.Fields.DocumentType, Value: 'Verrechnung' }]
    },
    "ArchivedInvoiceInbox": {
      PageTitle: "Einnahme (gebucht)",
      Disabled: false,
      NavPageName: "FDRW_Kam_Search",
      QueryConstraints: [
        {
          "FieldName": "Einnahme_Ausgabe",
          "Value": "Einnahme"
        }]
    },
    "ArchivedInvoiceOutbox": {
      PageTitle: "Ausgabe (gebucht)",
      Disabled: false,
      NavPageName: "FDRW_Kam_Search",
      QueryConstraints: [
        {
          "FieldName": "Einnahme_Ausgabe",
          "Value": "Ausgabe"
        }]
    },
    ArchivedBWF: {
      PageTitle: "Auftrag (gebucht)",
      Disabled: false,
      NavPageName: "FDRW_Kam_Auftrag_Search",
      QueryConstraints: []
    },
    RunningBWF: {
      PageTitle: "Auftrag (in Arbeit)",
      Disabled: false,
      NavPageName: rwf3pages.FDRW_Workflow_Invoice_Ledger.PageName,
      // TODO : Mittelreservierung <> 'BEstellung'
      QueryConstraints: [{ FieldName: rwf3pages.FDRW_Workflow_Invoice_Ledger.Fields.DocumentType, Value: '@*Mittelreservierung*' }]
    }
  },
  SearchFields: [
    "Abgabenart_Verkaufsklasse",
    "Adressnr",
    "Bankkontonummer",
    "Bankleitzahlcode",
    "Document_Date",
    "Document_No",
    "User_ID",
    "Datum_der_Verbuchung",
    "Description",
    "Description_2",
    "Order_No",
    "Amount",
    "SWIFT_Code",
    "Posting_Description",
    "Posting_Date",
    "Receipt_Date",
    "_Code",
    "Erweiterter_Belegtext",
    "External_Document_No",
    "Due_Date",
    "CustProp1",
    "CustProp2",
    "CustProp3",
    "CustProp4",
    "CustProp5",
    "Gemeindenr",
    "IBAN",
    "CheckedOutBy",
    "Investitionsnr",
    "Allocation_Template",
    "Global_Dimension_1_Name",
    "Global_Dimension_1_Code",
    "Global_Dimension_2_Name",
    "Global_Dimension_2_Code",
    "Mahnart",
    "Mahnmethodencode",
    "Mandatsreferenz",
    "Mandatsreferenz_ext_Verfahren",
    "Name_2_Adresse",
    "Name_3_Adresse",
    "_Name",
    "No",
    "Objektnummer",
    "Open",
    "City",
    "PLZ",
    "RemainingAmount",
    "VAT_Reporting_Date",
    "G_L_Account_Name",
    "G_L_Account_No",
    "Pmt_Discount_Date",
    "Reversed",
    "Address",
    "CurrentWorkflowPart_Name",
    "CurrentWorkflowUser",
    "Vorgang_No",
    "WorkflowID",
    "CurrentWorkflowStep_Name",
    "View",
    // Kamerale Felder
    "Abgabenart",
    "HHJahr",
    "Haushalt",
    "Gliederung",
    "Gliederungsergänzung",
    "Gruppierung",
    "Gruppierungsergänzung",
    "Name_Adresse",
    "Kassenzeichen",
    "Buchungstext",
    "Belegnr",
    "HÜL_Nr",
    "Einnahme_Ausgabe",
    "Überweisungstext",
    "Überweisungstext_2",
    "Überweisungstext_3",
    "Überweisungstext_4",
    "Überweisungstext_5",
    "DocumentTotalAmount",
    "Zahlungsformcode",
    "Unterkonto",
    "EntryNo",
    "Name_2",
    "Search_Name"],

  DefaultSignatureSettings: {
  },

  UserColumns: [{
    Page: [wfMyInstances.PageName, wfAllInstances.PageName, FDRW_Workflow_Running_Instances],
    Fields: [wfMyInstances.Fields.CheckedOutBy, wfMyInstances.Fields.LastChangedBy, wfMyInstances.Fields.CurrentWorkflowUser]
  }, {
    Page: rwf3pages.FDRW_WFInstanceCompleted.PageName + "." + rwf3pages.FDRW_WFInstanceCompleted.Fields.Workflow_Archive_Document_Sub,
    Fields: ["AddedBy"]
  }],

  TileSettings: {
    Thresholds: { Small: 10, Medium: 100, Large: 500 }
  },

  LookupSettings: {
    MaxRead: 100,
    MinSearch: 0
  },

  CustomCaptions: {
    Search: {
      Eingang: "Eingang",
      Ausgang: "Ausgang",
      Auftrag: "Bestellung",
    },
    Search_Kam: {
      Eingang: "Einnahme",
      Ausgang: "Ausgabe",
      Auftrag: "Auftrag",
    }
  }
};
